import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';

const TemplateSettings = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('quotation'); // 'quotation' or 'vendor'
  const [saving, setSaving] = useState(false);
  
  // Quotation Template
  const [quotationTemplate, setQuotationTemplate] = useState({
    companyName: 'Your Company Name',
    companyAddress: 'Company Address',
    companyPhone: '+91 XXXXXXXXXX',
    companyEmail: 'info@company.com',
    companyWebsite: 'www.company.com',
    logoUrl: '',
    headerColor: '#667eea',
    accentColor: '#764ba2',
    showTermsAndConditions: true,
    showCancellationPolicy: true,
    showPaymentTerms: true,
    footerText: 'Thank you for choosing us!',
    customCSS: ''
  });
  
  // Vendor Email Template
  const [vendorTemplate, setVendorTemplate] = useState({
    subject: '🔄 REVISION REQUEST - {quotationNumber} (v{version}) - {customerName}',
    greeting: 'Dear Vendor,',
    introText: 'This is a REVISION REQUEST for an existing quotation. The customer has requested changes to the original booking.',
    showOriginalDetails: true,
    showRevisionNotes: true,
    showActionRequired: true,
    urgentMessage: '⚠️ URGENT: This is a HIGH PRIORITY request. Please respond within 2-4 hours.',
    closingText: 'Thank you for your prompt attention to this revision request.',
    signature: 'Best regards,\nReservation Team\nTours & Travels'
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('companyToken') || 
                    localStorage.getItem('employeeToken');
      
      // Load saved templates from backend or localStorage
      const savedQuotation = localStorage.getItem('quotationTemplate');
      const savedVendor = localStorage.getItem('vendorTemplate');
      
      if (savedQuotation) {
        setQuotationTemplate(JSON.parse(savedQuotation));
      }
      if (savedVendor) {
        setVendorTemplate(JSON.parse(savedVendor));
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const saveTemplates = async () => {
    try {
      setSaving(true);
      
      // Save to localStorage (can be extended to save to backend)
      localStorage.setItem('quotationTemplate', JSON.stringify(quotationTemplate));
      localStorage.setItem('vendorTemplate', JSON.stringify(vendorTemplate));
      
      alert('✅ Templates saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving templates:', error);
      alert('❌ Error saving templates');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    if (activeTab === 'quotation') {
      setQuotationTemplate({
        companyName: 'Your Company Name',
        companyAddress: 'Company Address',
        companyPhone: '+91 XXXXXXXXXX',
        companyEmail: 'info@company.com',
        companyWebsite: 'www.company.com',
        logoUrl: '',
        headerColor: '#667eea',
        accentColor: '#764ba2',
        showTermsAndConditions: true,
        showCancellationPolicy: true,
        showPaymentTerms: true,
        footerText: 'Thank you for choosing us!',
        customCSS: ''
      });
    } else {
      setVendorTemplate({
        subject: '🔄 REVISION REQUEST - {quotationNumber} (v{version}) - {customerName}',
        greeting: 'Dear Vendor,',
        introText: 'This is a REVISION REQUEST for an existing quotation. The customer has requested changes to the original booking.',
        showOriginalDetails: true,
        showRevisionNotes: true,
        showActionRequired: true,
        urgentMessage: '⚠️ URGENT: This is a HIGH PRIORITY request. Please respond within 2-4 hours.',
        closingText: 'Thank you for your prompt attention to this revision request.',
        signature: 'Best regards,\nReservation Team\nTours & Travels'
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">⚙️ Template Settings</h2>
            <p className="text-sm opacity-90">Customize quotation and email templates</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('quotation')}
              className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                activeTab === 'quotation'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📄 Quotation PDF Template
            </button>
            <button
              onClick={() => setActiveTab('vendor')}
              className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                activeTab === 'vendor'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📧 Vendor Email Template
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Quotation Template */}
          {activeTab === 'quotation' && (
            <div className="space-y-6">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">📄 Quotation PDF Settings</h3>
                <p className="text-sm text-purple-700">Customize how your quotation PDFs look</p>
              </div>

              {/* Company Information */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-800">🏢 Company Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Company Name</label>
                    <input
                      type="text"
                      value={quotationTemplate.companyName}
                      onChange={(e) => setQuotationTemplate({...quotationTemplate, companyName: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <input
                      type="text"
                      value={quotationTemplate.companyPhone}
                      onChange={(e) => setQuotationTemplate({...quotationTemplate, companyPhone: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input
                      type="email"
                      value={quotationTemplate.companyEmail}
                      onChange={(e) => setQuotationTemplate({...quotationTemplate, companyEmail: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Website</label>
                    <input
                      type="text"
                      value={quotationTemplate.companyWebsite}
                      onChange={(e) => setQuotationTemplate({...quotationTemplate, companyWebsite: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Address</label>
                    <textarea
                      value={quotationTemplate.companyAddress}
                      onChange={(e) => setQuotationTemplate({...quotationTemplate, companyAddress: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                      rows="2"
                    />
                  </div>
                </div>
              </div>

              {/* Colors */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-800">🎨 Colors</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Header Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={quotationTemplate.headerColor}
                        onChange={(e) => setQuotationTemplate({...quotationTemplate, headerColor: e.target.value})}
                        className="w-16 h-10 border rounded"
                      />
                      <input
                        type="text"
                        value={quotationTemplate.headerColor}
                        onChange={(e) => setQuotationTemplate({...quotationTemplate, headerColor: e.target.value})}
                        className="flex-1 border rounded px-3 py-2"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Accent Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={quotationTemplate.accentColor}
                        onChange={(e) => setQuotationTemplate({...quotationTemplate, accentColor: e.target.value})}
                        className="w-16 h-10 border rounded"
                      />
                      <input
                        type="text"
                        value={quotationTemplate.accentColor}
                        onChange={(e) => setQuotationTemplate({...quotationTemplate, accentColor: e.target.value})}
                        className="flex-1 border rounded px-3 py-2"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Display Options */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-800">👁️ Display Options</h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={quotationTemplate.showTermsAndConditions}
                      onChange={(e) => setQuotationTemplate({...quotationTemplate, showTermsAndConditions: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Show Terms & Conditions</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={quotationTemplate.showCancellationPolicy}
                      onChange={(e) => setQuotationTemplate({...quotationTemplate, showCancellationPolicy: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Show Cancellation Policy</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={quotationTemplate.showPaymentTerms}
                      onChange={(e) => setQuotationTemplate({...quotationTemplate, showPaymentTerms: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Show Payment Terms</span>
                  </label>
                </div>
              </div>

              {/* Footer Text */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-800">📝 Footer Text</h4>
                <textarea
                  value={quotationTemplate.footerText}
                  onChange={(e) => setQuotationTemplate({...quotationTemplate, footerText: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  rows="2"
                  placeholder="Thank you message..."
                />
              </div>
            </div>
          )}

          {/* Vendor Email Template */}
          {activeTab === 'vendor' && (
            <div className="space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">📧 Vendor Email Settings</h3>
                <p className="text-sm text-blue-700">Customize vendor revision request emails</p>
                <p className="text-xs text-blue-600 mt-2">
                  Available variables: {'{quotationNumber}'}, {'{version}'}, {'{customerName}'}, {'{destination}'}
                </p>
              </div>

              {/* Email Subject */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-800">📬 Email Subject</h4>
                <input
                  type="text"
                  value={vendorTemplate.subject}
                  onChange={(e) => setVendorTemplate({...vendorTemplate, subject: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Email subject line..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Preview: {vendorTemplate.subject.replace('{quotationNumber}', 'QT-156').replace('{version}', '2').replace('{customerName}', 'John Doe')}
                </p>
              </div>

              {/* Greeting */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-800">👋 Greeting</h4>
                <input
                  type="text"
                  value={vendorTemplate.greeting}
                  onChange={(e) => setVendorTemplate({...vendorTemplate, greeting: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Dear Vendor,"
                />
              </div>

              {/* Introduction Text */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-800">📝 Introduction Text</h4>
                <textarea
                  value={vendorTemplate.introText}
                  onChange={(e) => setVendorTemplate({...vendorTemplate, introText: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  rows="3"
                  placeholder="Introduction message..."
                />
              </div>

              {/* Display Options */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-800">👁️ Display Options</h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={vendorTemplate.showOriginalDetails}
                      onChange={(e) => setVendorTemplate({...vendorTemplate, showOriginalDetails: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Show Original Quotation Details</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={vendorTemplate.showRevisionNotes}
                      onChange={(e) => setVendorTemplate({...vendorTemplate, showRevisionNotes: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Show Revision Notes</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={vendorTemplate.showActionRequired}
                      onChange={(e) => setVendorTemplate({...vendorTemplate, showActionRequired: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Show Action Required Section</span>
                  </label>
                </div>
              </div>

              {/* Urgent Message */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-800">⚠️ Urgent Priority Message</h4>
                <textarea
                  value={vendorTemplate.urgentMessage}
                  onChange={(e) => setVendorTemplate({...vendorTemplate, urgentMessage: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  rows="2"
                  placeholder="Message for high priority requests..."
                />
                <p className="text-xs text-gray-500 mt-1">This appears only for HIGH priority revisions</p>
              </div>

              {/* Closing Text */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-800">👋 Closing Text</h4>
                <textarea
                  value={vendorTemplate.closingText}
                  onChange={(e) => setVendorTemplate({...vendorTemplate, closingText: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  rows="2"
                  placeholder="Closing message..."
                />
              </div>

              {/* Signature */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-800">✍️ Email Signature</h4>
                <textarea
                  value={vendorTemplate.signature}
                  onChange={(e) => setVendorTemplate({...vendorTemplate, signature: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  rows="3"
                  placeholder="Best regards,&#10;Your Name&#10;Company Name"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t flex justify-between items-center">
          <button
            onClick={resetToDefault}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded"
          >
            🔄 Reset to Default
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-6 py-2 border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={saveTemplates}
              disabled={saving}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded hover:from-purple-700 hover:to-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : '💾 Save Templates'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateSettings;
